import { RefObject, useCallback, useEffect, useState } from 'react'

// https://stackoverflow.com/questions/28576636/mouse-click-and-drag-instead-of-horizontal-scroll-bar-to-view-full-content-of-c
export const useHorizontalScroll = (ref: RefObject<HTMLElement>) => {
    const [isDown, setIsDown] = useState(false)
    const [isScrolling, setIsScrolling] = useState(false)
    const [startX, setStartX] = useState(0)
    const [scrollLeft, setScrollLeft] = useState(0)

    const startDragging = useCallback(
        (e: MouseEvent | TouchEvent) => {
            if (!ref.current) return

            const pageX = e instanceof TouchEvent ? e.touches[0].pageX : e.pageX

            setIsDown(true)
            setStartX(pageX - ref.current.offsetLeft)
            setScrollLeft(ref.current.scrollLeft)
        },
        [ref],
    )

    const stopDragging = useCallback(() => {
        setIsDown(false)

        // Add delay to prevent click too early after dragging
        setTimeout(() => {
            setIsScrolling(false)
        }, 50)
    }, [])

    const drag = useCallback(
        (e: MouseEvent | TouchEvent) => {
            e.preventDefault()
            if (!isDown) return
            if (!ref.current) return

            setIsScrolling(true)

            const pageX = e instanceof TouchEvent ? e.touches[0].pageX : e.pageX

            const x = pageX - ref.current.offsetLeft
            const scroll = x - startX
            ref.current.scrollLeft = scrollLeft - scroll
        },
        [isDown, ref, scrollLeft, startX],
    )

    const handleClick = useCallback(
        (e) => {
            if (isScrolling) {
                e.preventDefault()
            }
        },
        [isScrolling],
    )

    useEffect(() => {
        const container = ref.current

        container?.addEventListener('mousemove', drag)
        container?.addEventListener('mousedown', startDragging)
        container?.addEventListener('mouseup', stopDragging)
        container?.addEventListener('mouseleave', stopDragging)

        container?.addEventListener('touchmove', drag)
        container?.addEventListener('touchstart', startDragging)
        container?.addEventListener('touchend', stopDragging)
        container?.addEventListener('touchcancel', stopDragging)

        container?.addEventListener('click', handleClick)

        return () => {
            container?.removeEventListener('mousemove', drag)
            container?.removeEventListener('mousedown', startDragging)
            container?.removeEventListener('mouseup', stopDragging)
            container?.removeEventListener('mouseleave', stopDragging)

            container?.removeEventListener('touchmove', drag)
            container?.removeEventListener('touchstart', startDragging)
            container?.removeEventListener('touchend', stopDragging)
            container?.removeEventListener('touchcancel', stopDragging)

            container?.removeEventListener('click', handleClick)
        }
    }, [drag, handleClick, ref, startDragging, stopDragging])
}
